import React, { useState, useEffect, useMemo } from "react";
import {
  Flex,
  Select,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Checkbox,
  Text,
  Alert,
  AlertIcon,
} from "@chakra-ui/react";
import { FiFilter } from "react-icons/fi";
import axiosInstance from "../../helpers/axiosInstance";
import Translate from "../../components/LanguageTranslation/Translate";
import { useTranslation } from "../../components/LanguageTranslation/TranslationContext";

const PackingListTrips = ({
  selectedTrip,
  setSelectedTrip,
  setTripName,
  onTripSelect,
}) => {
  const { useTranslate } = useTranslation();
  const placeholderText = useTranslate("Select a trip");

  const [trips, setTrips] = useState([]);
  const [error, setError] = useState(null);
  // keep a Set of selected statuses; empty Set === "all"
  const [selectedStatuses, setSelectedStatuses] = useState(
    new Set(["personal", "shared"])
  );

  /* ----------------------------------------------------------------
   * 1. Fetch trips (once)
   * ---------------------------------------------------------------- */
  useEffect(() => {
    (async () => {
      try {
        const res = await axiosInstance.get(
          "/packing_lists/packing-lists-by-user-email"
        );
        // console.log("res", res);

        // Handle the correct response structure
        if (res.data.success && res.data.data?.packing_lists) {
          // Transform the packing_lists data to match the expected format
          const transformedTrips = res.data.data.packing_lists.map((trip) => ({
            _id: trip.trip_id,
            general_info: {
              itinerary: {
                trip_name: trip.trip_name,
              },
            },
            status: "personal", // Default status since it's not provided in the response
            category: "itinerary", // Default category
          }));
          setTrips(transformedTrips);
        } else {
          setTrips([]);
        }
      } catch (err) {
        console.error("Error fetching trips:", err);
        // setError("Failed to fetch trips. Please try again.");
        setTrips([]);
      }
    })();
  }, []);

  /* ----------------------------------------------------------------
   * 2. Filter trips by status
   * ---------------------------------------------------------------- */
  const filteredTrips = useMemo(() => {
    // empty Set = show everything
    if (selectedStatuses.size === 0) return trips;
    return trips.filter((t) =>
      selectedStatuses.has((t.status || "").toLowerCase())
    );
  }, [trips, selectedStatuses]);

  /* ----------------------------------------------------------------
   * 3. Toggle helpers
   * ---------------------------------------------------------------- */
  const toggleStatus = (status) =>
    setSelectedStatuses((prev) => {
      const next = new Set(prev);
      next.has(status) ? next.delete(status) : next.add(status);
      return next;
    });

  const toggleAll = () =>
    setSelectedStatuses((prev) =>
      // if "all" already selected (0 or both), clear; else select both
      prev.size === 0 || prev.size === 2
        ? new Set()
        : new Set(["personal", "shared"])
    );

  /* ----------------------------------------------------------------
   * 4. Handle selection
   * ---------------------------------------------------------------- */
  const handleChange = (e) => {
    const id = e.target.value;
    const trip = trips.find((t) => t._id === id);
    setSelectedTrip(id);
    setTripName(trip?.general_info?.itinerary?.trip_name || "Unknown Trip");
    onTripSelect(id);
  };

  // If there's an error, show the error message
  if (error) {
    return (
      <Alert status="warning" borderRadius="lg" mb={4}>
        <AlertIcon />
        <Text fontSize="sm">{error}</Text>
      </Alert>
    );
  }

  return (
    <Flex align="center" gap={2}>
      {/* FILTER BUTTON */}
      <Menu closeOnSelect={false}>
        <MenuButton
          as={IconButton}
          icon={<FiFilter />}
          size="sm"
          borderRadius="xl"
          color="#286328"
          variant={selectedStatuses.size === 0 ? "outline" : "solid"}
          aria-label="Filter trips"
        />
        <MenuList minW="150px" py={1}>
          {/* All */}
          <MenuItem py={1} onClick={toggleAll}>
            <Checkbox
              isChecked={
                selectedStatuses.size === 0 || selectedStatuses.size === 2
              }
              pointerEvents="none"
              mr={2}
            />
            <Translate text="All trips" />
          </MenuItem>

          {/* Personal */}
          <MenuItem py={1} onClick={() => toggleStatus("personal")}>
            <Checkbox
              isChecked={selectedStatuses.has("personal")}
              pointerEvents="none"
              mr={2}
            />
            <Translate text="Personal" />
          </MenuItem>

          {/* Shared */}
          <MenuItem py={1} onClick={() => toggleStatus("shared")}>
            <Checkbox
              isChecked={selectedStatuses.has("shared")}
              pointerEvents="none"
              mr={2}
            />
            <Translate text="Shared" />
          </MenuItem>
        </MenuList>
      </Menu>

      {/* MAIN DROPDOWN */}
      <Select
        placeholder={placeholderText}
        value={selectedTrip}
        onChange={handleChange}
        size="sm"
        borderRadius="xl"
        flex="1"
      >
        {filteredTrips.map((t) => (
          <option key={t._id} value={t._id}>
            <Translate text={t.general_info.itinerary.trip_name} />
          </option>
        ))}
      </Select>
    </Flex>
  );
};

export default PackingListTrips;
